import React from 'react'
import {useIntl} from 'react-intl'

import {
  AssistantClarification,
  AssistantClarificationOption,
} from '../../modes/assistant/assistantResponseContract'

type AssistantClarificationChipsProps = {
  clarification: AssistantClarification
  /**
   * A pick. Options carrying a `capabilityId` answer the question outright; a slot option only
   * names what is still missing, so the caller puts the reader in the composer instead.
   */
  onSelect: (option: AssistantClarificationOption) => void
  disabled?: boolean
}

/**
 * The options of a `clarify` / `menu` turn, rendered under the question the assistant asked.
 *
 * The chips are a convenience, not the mechanism: L0 already binds "the second one", "2" and
 * the option label typed as text. What is load-bearing is the echo of the clarification on the
 * next turn (see `sanitizeClarificationForEcho`) — the backend is stateless per turn, so
 * without it the answer to a question we asked cannot be bound to it.
 */
const AssistantClarificationChips: React.FC<AssistantClarificationChipsProps> = ({
  clarification,
  onSelect,
  disabled = false,
}) => {
  const intl = useIntl()

  const options = clarification.options ?? []
  if (options.length === 0) return null

  // Every chip sends its label verbatim as the next query — the backend matches the reply to its
  // options by label, for slot clarifications too. `kind` only decides the intro wording.
  const isSlotKind = clarification.kind === 'slot'
  const introId = isSlotKind
    ? 'CHATBOT.CLARIFY.SLOT_LABEL'
    : clarification.kind === 'menu'
      ? 'CHATBOT.CLARIFY.MENU_LABEL'
      : 'CHATBOT.CLARIFY.CAPABILITY_LABEL'

  return (
    <div className='chatbot-clarify'>
      <div className='chatbot-clarify__intro'>{intl.formatMessage({id: introId})}</div>
      <div
        className={`chatbot-clarify__options${isSlotKind ? ' chatbot-clarify__options--slots' : ''}`}
        role='list'
        aria-label={intl.formatMessage({id: 'CHATBOT.CLARIFY.OPTIONS_ARIA'})}
      >
        {options.map((option, index) => (
          <button
            key={option.capabilityId || option.slot || `${option.label}-${index}`}
            type='button'
            role='listitem'
            className='chatbot-clarify__option'
            disabled={disabled}
            onClick={() => onSelect(option)}
          >
            <span className='chatbot-clarify__option-label'>{option.label}</span>
            {option.description ? (
              <span className='chatbot-clarify__option-description'>{option.description}</span>
            ) : null}
          </button>
        ))}
      </div>
    </div>
  )
}

export default AssistantClarificationChips
